import { apiClient } from "./client";
import endpoint from "@/constants/endpoint";
import type { AuthUser } from "@chinese-vendor/schemas";
import type { ApplicationDetail } from "./review";
import { formatSplitDateTime, formatRelativeTime, formatAbsoluteDate } from "@/lib/dateFormat";
import type { SupportedLocale } from "@chinese-vendor/i18n";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PendingUpdateField {
  /** i18n key for the field label shown as the row title. */
  name: string;
  /** i18n key for the section/category label shown below the name. */
  category: string;
  /** The application stage this field belongs to. */
  stageId: ApplicationStageId;
  /** The field key used to look up the field in FIELD_MAP and navigate to the update screen. */
  fieldKey: string;
}

export interface RejectedDocumentData {
  /** i18n key for the document field label shown as a bold heading. */
  name: string;
  /** Text value (e.g. a code number or rejection reason). */
  identifier?: string;
  /** When the document is a file upload — file metadata. */
  file?: { name: string; size: string; url?: string };
}

export interface VendorDashboardData {
  applicationStatus: "pending" | "approved" | "rejected" | "update_required";
  /** ISO date string representing the most relevant status date. */
  statusDate: string;
  /** Shown in the error card when status is "rejected". */
  rejectionReason?: string;
  /** Documents / fields flagged during review (only when status is "rejected"). */
  rejectedDocuments?: RejectedDocumentData[];
  /** Fields that require vendor updates (only when status is "update_required"). */
  pendingUpdates?: PendingUpdateField[];
}

export type ApplicationStageId =
  | "legal_representative"
  | "business_information"
  | "product_compliance"
  | "financial_settlement"
  | "branding_localization";

export type ApplicationStageStatus =
  | "completed"
  | "in_progress"
  | "not_started"
  | "update_required";

export interface ApplicationStageData {
  id: ApplicationStageId;
  status: ApplicationStageStatus;
}

export interface ApplicationDateInfo {
  /** Human-friendly relative time, e.g. "2 days ago". */
  relative: string;
  /** Formatted absolute date, e.g. "Mar 02, 2026". */
  absolute: string;
  /** Formatted time, e.g. "02:28 PM". */
  time: string;
}

export interface VendorApplicationData {
  companyName: string;
  primaryIndustry: string;
  lastActivity: ApplicationDateInfo | null;
  dateSubmitted: ApplicationDateInfo | null;
  dateApproved: ApplicationDateInfo | null;
  stages: ApplicationStageData[];
}

// ─── Activity ─────────────────────────────────────────────────────────────────

export type ActivityEventType =
  | "application_received"
  | "application_approved"
  | "application_rejected"
  | "update_required"
  | "update_submitted";

export interface ActivityEvent {
  type: ActivityEventType;
  /** Dynamic description provided by the API (already localised). */
  description: string;
  /** Formatted date, e.g. "15/03/2026". */
  date: string;
  /** Formatted time, e.g. "02:28 PM". */
  time: string;
}

export interface VendorActivityData {
  events: ActivityEvent[];
}

// ─── Internal mappings ────────────────────────────────────────────────────────

/** Maps backend `action` strings to frontend ActivityEventType. */
const ACTION_TO_EVENT_TYPE: Record<string, ActivityEventType> = {
  submitted: "application_received",
  resubmitted: "update_submitted",
  approve: "application_approved",
  reject: "application_rejected",
  request_changes: "update_required",
  undo_decision: "application_received",
};

/** i18n key for each stage label. */
const STAGE_LABEL_KEYS: Record<ApplicationStageId, string> = {
  legal_representative: "vendor.application.stages.legalRepresentative",
  business_information: "vendor.application.stages.businessInformation",
  product_compliance: "vendor.application.stages.productCompliance",
  financial_settlement: "vendor.application.stages.financialSettlement",
  branding_localization: "vendor.application.stages.brandingLocalization",
};

/** Maps each rejectable field key to its stage and i18n label key.
 * Keys must match the fully-qualified format used by the backend: "{step}.{field}".
 */
const FIELD_STAGE_MAP: Record<string, { stageId: ApplicationStageId; labelKey: string }> = {
  "step1.legal_representative_id_document": {
    stageId: "legal_representative",
    labelKey: "preVetting.step1.idType",
  },
  "step2.company_name_english": {
    stageId: "business_information",
    labelKey: "preVetting.step2.companyNameEnglish",
  },
  "step2.unified_social_credit_code": {
    stageId: "business_information",
    labelKey: "preVetting.step2.unifiedSocialCreditCode",
  },
  // Legacy entry: kept until the backend stops emitting this rejection key so
  // that vendors with pre-existing reviews are not silently dropped from the
  // pending-updates list.
  "step2.tax_registration_number": {
    stageId: "business_information",
    labelKey: "preVetting.step2.taxRegistrationNumber",
  },
  "step2.business_license_document": {
    stageId: "business_information",
    labelKey: "preVetting.step2.businessLicense",
  },
  "step3.industry": {
    stageId: "product_compliance",
    labelKey: "preVetting.step3.industries",
  },
  "step3.product_subcategories": {
    stageId: "product_compliance",
    labelKey: "preVetting.step3.productSubcategories",
  },
  "step3.iso_certification_document": {
    stageId: "product_compliance",
    labelKey: "preVetting.step3.isoCertification",
  },
  "step3.ce_marking_document": {
    stageId: "product_compliance",
    labelKey: "preVetting.step3.ceMarking",
  },
  "step3.quality_test_report_document": {
    stageId: "product_compliance",
    labelKey: "preVetting.step3.qualityTestReport",
  },
  "step3.eco_certification_document": {
    stageId: "product_compliance",
    labelKey: "preVetting.step3.ecoCertification",
  },
  "step4.alipay_merchant_id": {
    stageId: "financial_settlement",
    labelKey: "preVetting.step4.alipay.merchantId",
  },
  "step4.wechatpay_merchant_id": {
    stageId: "financial_settlement",
    labelKey: "preVetting.step4.wechatPay.merchantId",
  },
  "step5.business_short_name_english": {
    stageId: "branding_localization",
    labelKey: "preVetting.step5.shortNameEnglish",
  },
  "step5.business_description_english": {
    stageId: "branding_localization",
    labelKey: "preVetting.step5.descriptionEnglish",
  },
  "step5.business_logo": {
    stageId: "branding_localization",
    labelKey: "preVetting.step5.businessLogo",
  },
};

/** Ordered list of all stage IDs for iteration. */
const STAGE_ORDER: ApplicationStageId[] = [
  "legal_representative",
  "business_information",
  "product_compliance",
  "financial_settlement",
  "branding_localization",
];

/** Maps a stage ID to the corresponding section key in ApplicationDetailResponse. */
const STAGE_TO_SECTION_KEY: Record<ApplicationStageId, string> = {
  legal_representative: "legalRepresentative",
  business_information: "businessInformation",
  product_compliance: "productCompliance",
  financial_settlement: "financialSettlement",
  branding_localization: "brandingLocalization",
};

// ─── Response transformers ────────────────────────────────────────────────────

/** Shapes a raw backend ApplicationDetailResponse into VendorDashboardData. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toDashboardData(raw: Record<string, any>): VendorDashboardData {
  const status: string = raw.status ?? "pending_review";
  const reviewChangeState: string | null = raw.reviewChangeState ?? null;
  const fieldRejections: Record<string, unknown> = raw.fieldRejections ?? {};

  let applicationStatus: VendorDashboardData["applicationStatus"] = "pending";
  if (status === "approved") {
    applicationStatus = "approved";
  } else if (status === "rejected") {
    applicationStatus = "rejected";
  } else if (status === "pending_review" && reviewChangeState === "changes_requested") {
    applicationStatus = "update_required";
  }

  const statusDate: string =
    (status === "approved" || status === "rejected" ? raw.decisionAt : null) ??
    raw.submittedAt ??
    raw.updatedAt ??
    new Date().toISOString();

  const result: VendorDashboardData = { applicationStatus, statusDate };

  if (applicationStatus === "rejected") {
    result.rejectionReason = raw.rejectionReason ?? undefined;
    result.rejectedDocuments = Object.keys(fieldRejections).map((fieldKey) => {
      const info = FIELD_STAGE_MAP[fieldKey];
      return {
        name: info?.labelKey ?? fieldKey,
        identifier: (fieldRejections[fieldKey] as Record<string, string>)?.reason ?? undefined,
      };
    });
  }

  if (applicationStatus === "update_required") {
    result.pendingUpdates = Object.keys(fieldRejections)
      .map((fieldKey): PendingUpdateField | null => {
        const info = FIELD_STAGE_MAP[fieldKey];
        if (!info) return null;
        return {
          name: info.labelKey,
          category: STAGE_LABEL_KEYS[info.stageId],
          stageId: info.stageId,
          fieldKey,
        };
      })
      .filter((f): f is PendingUpdateField => f !== null);
  }

  return result;
}

/** Shapes a raw backend ApplicationDetailResponse into VendorApplicationData. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toApplicationData(raw: Record<string, any>, locale: SupportedLocale): VendorApplicationData {
  const fieldRejections: Record<string, unknown> = raw.fieldRejections ?? {};
  const reviewChangeState: string | null = raw.reviewChangeState ?? null;

  const stages: ApplicationStageData[] = STAGE_ORDER.map((stageId) => {
    const sectionKey = STAGE_TO_SECTION_KEY[stageId];
    const hasData = raw[sectionKey] != null;

    if (!hasData) {
      return { id: stageId, status: "not_started" as ApplicationStageStatus };
    }

    const stageFieldKeys = Object.keys(FIELD_STAGE_MAP).filter(
      (k) => FIELD_STAGE_MAP[k].stageId === stageId
    );
    const hasRejectedField =
      reviewChangeState === "changes_requested" &&
      stageFieldKeys.some((k) => k in fieldRejections);

    return {
      id: stageId,
      status: hasRejectedField ? "update_required" : "completed",
    };
  });

  const toDateInfo = (iso: string | null | undefined): ApplicationDateInfo | null => {
    if (!iso) return null;
    const { date, time } = formatSplitDateTime(iso, locale);
    return {
      relative: formatRelativeTime(iso, locale),
      absolute: formatAbsoluteDate(iso, locale),
      time,
    };
  };

  const lastActivityIso: string | null =
    raw.activityLog?.length > 0
      ? (raw.activityLog[raw.activityLog.length - 1].timestamp ?? null)
      : null;

  return {
    companyName: raw.companyName ?? "",
    primaryIndustry: raw.primaryIndustry ?? "",
    dateSubmitted: toDateInfo(raw.submittedAt),
    dateApproved: toDateInfo(raw.decisionAt),
    lastActivity: toDateInfo(lastActivityIso ?? raw.updatedAt),
    stages,
  };
}

/** Shapes a raw backend ActivityLogResponse into VendorActivityData. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toActivityData(raw: Record<string, any>, locale: SupportedLocale): VendorActivityData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items: any[] = Array.isArray(raw.items) ? raw.items : [];
  const events: ActivityEvent[] = items
    .map((item) => {
      const eventType = ACTION_TO_EVENT_TYPE[item.action as string];
      if (!eventType) return null;
      const { date, time } = formatSplitDateTime(item.created_at, locale);
      return {
        type: eventType,
        description: item.description ?? "",
        date,
        time,
      };
    })
    .filter((e): e is ActivityEvent => e !== null);
  return { events };
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const vendorApi = {
  /** Staff/Admin: Send an invite to a vendor email */
  async invite(email: string): Promise<void> {
    await apiClient.post<null>(endpoint.vendors.invite, { email });
  },

  /** Get the current vendor's profile (includes application_id when available). */
  async getProfile(): Promise<AuthUser> {
    const res = await apiClient.get<AuthUser>(endpoint.vendors.me);
    return res.data;
  },

  /** Persist the vendor's preferred locale — called from the language selection screen */
  async setLocalePreference(locale: string): Promise<void> {
    await apiClient.patch<null>(endpoint.vendors.setLocale, { locale });
  },

  /**
   * Get the vendor's home dashboard info (application status + rejected docs).
   * Derived from the full application detail response.
   */
  async getDashboardInfo(applicationId: string): Promise<VendorDashboardData> {
    const res = await apiClient.get<Record<string, unknown>>(
      endpoint.vendors.application(applicationId)
    );
    return toDashboardData(res.data as Record<string, unknown>);
  },

  /**
   * Get the vendor's application summary (company info + stage statuses + dates).
   * Derived from the full application detail response.
   */
  async getApplicationData(
    applicationId: string,
    locale: SupportedLocale = "en"
  ): Promise<VendorApplicationData> {
    const res = await apiClient.get<Record<string, unknown>>(
      endpoint.vendors.application(applicationId)
    );
    return toApplicationData(res.data as Record<string, unknown>, locale);
  },

  /** Get the vendor's own full application detail (field values + field rejections). */
  async getApplicationDetail(applicationId: string): Promise<ApplicationDetail> {
    const res = await apiClient.get<ApplicationDetail>(
      endpoint.vendors.application(applicationId)
    );
    return res.data;
  },

  /** Get the vendor's activity log (timeline of application events). */
  async getActivityData(
    applicationId: string,
    locale: SupportedLocale = "en"
  ): Promise<VendorActivityData> {
    const res = await apiClient.get<Record<string, unknown>>(
      endpoint.vendors.applicationActivity(applicationId)
    );
    return toActivityData(res.data as Record<string, unknown>, locale);
  },

  /** Submit or update a field-level review response (vendor-side). */
  async submitFieldReview(
    applicationId: string,
    payload: { field_key: string; status: "approved" | "rejected"; reason?: string }
  ): Promise<void> {
    await apiClient.put<null>(endpoint.vendors.applicationFieldReview(applicationId), payload);
  },

  /** Undo a previously submitted field-level review response. */
  async undoFieldReview(applicationId: string, fieldKey: string): Promise<void> {
    await apiClient.delete<null>(endpoint.vendors.applicationFieldReview(applicationId), {
      field_key: fieldKey,
    });
  },

  /** Submit or update a field value edit (vendor-side). */
  async submitFieldEdit(
    applicationId: string,
    payload: { field_key: string; value: string }
  ): Promise<void> {
    await apiClient.put<null>(endpoint.vendors.applicationFieldEdit(applicationId), payload);
  },

  /** Undo a previously submitted field value edit. */
  async undoFieldEdit(applicationId: string, fieldKey: string): Promise<void> {
    await apiClient.delete<null>(endpoint.vendors.applicationFieldEdit(applicationId), {
      field_key: fieldKey,
    });
  },
};
